/**
 * Data Grouping Service for Mini Printer
 * Groups raw Excel rows by a designated column (e.g., PO Number or Order Code) into discrete documents.
 */

import { isRowMetaKey } from "@/types/excel";

/** Joins the parts of a composite grouping key, e.g. `PO001|KHO-A`. */
export const GROUP_KEY_SEPARATOR = "|";

/** Group value for rows missing a grouping value. */
export const UNGROUPED_VALUE = "KHONG_MA";

/**
 * Build the grouping key of a row. A row missing any part of the key lands in
 * the special ungrouped bucket rather than silently joining another group.
 */
function groupKeyOf(row: Record<string, unknown>, colKeys: string[]): string {
  if (colKeys.length === 0) return UNGROUPED_VALUE;
  const parts: string[] = [];
  for (const key of colKeys) {
    const raw = row[key];
    if (raw == null || String(raw).trim() === "") return UNGROUPED_VALUE;
    parts.push(String(raw).trim());
  }
  return parts.join(GROUP_KEY_SEPARATOR);
}

export interface GroupedDocument {
  documentId: string;
  groupKey: string;
  groupValue: string;
  headerRow: Record<string, unknown>;
  itemRows: Record<string, unknown>[];
  totalItems: number;
}

/**
 * Group raw Excel rows by a specific grouping column.
 * If groupColumn is omitted or empty, each row becomes an individual document with 1 item row.
 */
/**
 * Detailed group with header values resolved from the first non-empty cell and
 * conflict warnings when a header field has multiple distinct values in a group.
 */
export interface DetailedGroup {
  documentId: string;
  groupKey: string;
  groupValue: string;
  /** Rows belonging to this group (in original order). */
  rows: Record<string, unknown>[];
  /** Header field -> first non-empty value across the group. */
  headerValues: Record<string, unknown>;
  /** Header field -> distinct non-empty values found in the group (length > 1 = conflict). */
  conflicts: Record<string, unknown[]>;
  conflictFields: string[];
  totalItems: number;
}

/**
 * Group raw Excel rows by a column and compute per-group header values
 * (first non-empty) plus conflict warnings for multi-valued header fields.
 * Empty grouping values are treated as a special "KHONG_MA" group.
 */
export function groupRowsDetailed(
  rows: Record<string, unknown>[],
  groupColumn?: string | string[]
): DetailedGroup[] {
  if (!rows || rows.length === 0) return [];

  const colKeys = (Array.isArray(groupColumn) ? groupColumn : [groupColumn ?? ""])
    .map((c) => (c ?? "").trim())
    .filter(Boolean);
  const groups = new Map<string, Record<string, unknown>[]>();
  const groupOrder: string[] = [];

  for (const row of rows) {
    const valStr = groupKeyOf(row, colKeys);
    if (!groups.has(valStr)) {
      groups.set(valStr, []);
      groupOrder.push(valStr);
    }
    groups.get(valStr)!.push(row);
  }

  return groupOrder.map((groupVal, idx) => {
    const groupRows = groups.get(groupVal) ?? [];
    const allKeys = new Set<string>();
    groupRows.forEach((r) =>
      Object.keys(r).forEach((k) => {
        if (!isRowMetaKey(k)) allKeys.add(k);
      })
    );

    const headerValues: Record<string, unknown> = {};
    const conflicts: Record<string, unknown[]> = {};

    for (const key of allKeys) {
      const values: unknown[] = [];
      for (const row of groupRows) {
        const v = row[key];
        if (v != null && String(v).trim() !== "") {
          values.push(v);
        }
      }
      headerValues[key] = values[0] ?? null;
      const distinct = Array.from(new Set(values.map((v) => String(v).trim())));
      // Only flag a real header conflict: the same field repeats a value in
      // some rows but disagrees in others. Columns that are unique on every
      // row (STT, Mã hàng…) are detail fields, not conflicts.
      if (distinct.length > 1 && distinct.length < values.length) {
        conflicts[key] = distinct;
      }
    }

    return {
      documentId: `doc-group-${idx + 1}`,
      groupKey: colKeys.join(GROUP_KEY_SEPARATOR) || "ROW",
      groupValue: groupVal,
      rows: groupRows,
      headerValues,
      conflicts,
      conflictFields: Object.keys(conflicts),
      totalItems: groupRows.length,
    };
  });
}

export function groupRowsByColumn(
  rows: Record<string, unknown>[],
  groupColumn?: string
): GroupedDocument[] {
  if (!rows || rows.length === 0) return [];

  if (!groupColumn || !groupColumn.trim()) {
    // No grouping column selected: 1 row = 1 document
    return rows.map((row, idx) => ({
      documentId: `doc-${idx + 1}`,
      groupKey: "ROW",
      groupValue: String(idx + 1),
      headerRow: row,
      itemRows: [row],
      totalItems: 1,
    }));
  }

  const colKey = groupColumn.trim();
  const groupsMap = new Map<string, Record<string, unknown>[]>();
  const groupOrder: string[] = [];

  for (const row of rows) {
    const rawVal = row[colKey];
    const valStr = rawVal != null && String(rawVal).trim() !== ""
      ? String(rawVal).trim()
      : "KHONG_MA";

    if (!groupsMap.has(valStr)) {
      groupsMap.set(valStr, []);
      groupOrder.push(valStr);
    }
    groupsMap.get(valStr)!.push(row);
  }

  return groupOrder.map((groupVal, idx) => {
    const itemRows = groupsMap.get(groupVal) ?? [];
    const headerRow = itemRows[0] ?? {};

    return {
      documentId: `doc-group-${idx + 1}`,
      groupKey: colKey,
      groupValue: groupVal,
      headerRow,
      itemRows,
      totalItems: itemRows.length,
    };
  });
}
